// One brush stroke on a model surface, for the shared physical paint engine.
//
// The engine's bristle kinematics (BristleBrush, PaletteKnife) work in a flat
// plane in cell units. On a model that plane is the surface's tangent plane,
// carried along the stroke by parallel transport: each pointer sample moves
// the virtual brush position by the world displacement projected onto the
// current tangent frame (in cells of the map's median texel size). The brush
// therefore sees one continuous stroke even where the UV layout jumps (seams)
// or stretches.
//
// Every stamp the kinematics emit is mapped from the plane onto the UV grid:
//   - onto the island under the brush (local linear map from the cell
//     Jacobian, so stretched or rotated UVs keep the stamp round on the model),
//   - and onto every other island within reach of the brush (the other side
//     of a seam), each restricted to its own island's cells by the kernel.
// Each island copy gets a share of the stamp's travel (ds) in proportion to
// the brush area that falls on it, so paint is not doubled along seams.

import { islandsNear, nearestCoveredCell, planeToGridMatrix } from './SurfaceMap.js';

const sub = (a, b) => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const dot = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const cross = (a, b) => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
const scale = (a, s) => [a[0] * s, a[1] * s, a[2] * s];
const norm = (a) => { const l = Math.hypot(a[0], a[1], a[2]); return l > 1e-12 ? scale(a, 1 / l) : null; };
const vec = (p) => (Array.isArray(p) ? p : [Number(p.x) || 0, Number(p.y) || 0, Number(p.z) || 0]);

function perpendicularTo(n) {
  const axis = Math.abs(n[0]) < 0.9 ? [1, 0, 0] : [0, 1, 0];
  return norm(cross(cross(n, axis), n)) || [1, 0, 0];
}

// map: SurfaceMap. radiusCells: brush radius in plane cells (for seam reach and shares).
// allowedMaterial >= 0 paints only islands of that material (paintable material).
export function createSurfaceStroke(map, { radiusCells = 8, cellWorld = map.texelWorld, allowedMaterial = -1 } = {}) {
  const h = cellWorld > 0 ? cellWorld : 1;
  const W = map.width;
  const H = map.height;
  let frame = null; // { P, N, T, B }
  let v = [0, 0]; // brush position in the plane (cells)
  let entries = []; // islands the current segment is mapped onto
  let primaryIsland = 0;
  const stats = { samples: 0, missed: 0, seamSamples: 0, stamps: 0, mappedStamps: 0 };

  const gridOf = (hit) => {
    const uv = Array.isArray(hit.uv) ? hit.uv : [Number(hit.uv.x), Number(hit.uv.y)];
    return [uv[0] * W, uv[1] * H];
  };

  const linearMap = (cell, T, B) => planeToGridMatrix(map, cell, T, B, h) || [1, 0, 0, 1];

  // Falloff weight of the brush disc (at the current plane position) on the
  // cells of copy e's island, divided by the copy's cell area scale.
  const footprintWeight = (e) => {
    const [m00, m01, m10, m11] = e.M;
    const qx = v[0] - e.anchorV[0];
    const qy = v[1] - e.anchorV[1];
    const cx = e.anchorGrid[0] + m00 * qx + m01 * qy;
    const cy = e.anchorGrid[1] + m10 * qx + m11 * qy;
    const area = Math.abs(m00 * m11 - m01 * m10) || 1;
    const r = Math.max(0.75, radiusCells * Math.sqrt(area));
    const r2 = r * r;
    const step = Math.max(1, Math.ceil(r / 40));
    let sum = 0;
    for (let y = Math.max(0, Math.floor(cy - r)); y <= Math.min(H - 1, Math.ceil(cy + r)); y += step) {
      for (let x = Math.max(0, Math.floor(cx - r)); x <= Math.min(W - 1, Math.ceil(cx + r)); x += step) {
        if (map.island[y * W + x] !== e.island) continue;
        const d2 = (x + 0.5 - cx) * (x + 0.5 - cx) + (y + 0.5 - cy) * (y + 0.5 - cy);
        if (d2 >= r2) continue;
        const f = 1 - d2 / r2;
        sum += f * f;
      }
    }
    return (sum * step * step) / area;
  };

  const buildEntries = (P, N, T, B, grid, cell) => {
    const reach = (radiusCells + 3) * h;
    const near = islandsNear(map, P, reach, N);
    const island = map.island[cell];
    const list = [{ island, anchorGrid: grid, anchorV: v.slice(), M: linearMap(cell, T, B), count: 0 }];
    // The patch under the brush is the one whose nearest cell is closest on
    // the grid to the hit; every other patch is across a seam.
    let primaryPatch = null;
    let best = Infinity;
    for (const found of near) {
      if (found.island !== island) continue;
      const dx = (found.cell % W) + 0.5 - grid[0];
      const dy = Math.floor(found.cell / W) + 0.5 - grid[1];
      if (dx * dx + dy * dy < best) { best = dx * dx + dy * dy; primaryPatch = found; }
    }
    for (const found of near) {
      if (found === primaryPatch) { list[0].count += found.count; continue; }
      const A = [map.pos[3 * found.cell], map.pos[3 * found.cell + 1], map.pos[3 * found.cell + 2]];
      const d = sub(A, P);
      if (found.island === island) {
        // Same island where the primary map already predicts it (stretched
        // UVs spread one patch further on the grid): part of the primary patch.
        const [m00, m01, m10, m11] = list[0].M;
        const qx = dot(d, T) / h;
        const qy = dot(d, B) / h;
        const px = grid[0] + m00 * qx + m01 * qy;
        const py = grid[1] + m10 * qx + m11 * qy;
        const gx = (found.cell % W) + 0.5;
        const gy = Math.floor(found.cell / W) + 0.5;
        if (Math.hypot(px - gx, py - gy) < Math.max(2.5, 0.3 * radiusCells)) { list[0].count += found.count; continue; }
      }
      list.push({
        island: found.island,
        anchorGrid: [(found.cell % W) + 0.5, Math.floor(found.cell / W) + 0.5],
        anchorV: [v[0] + dot(d, T) / h, v[1] + dot(d, B) / h],
        M: linearMap(found.cell, T, B),
        count: found.count,
      });
    }
    // The hash samples every other cell; a tiny contact still counts.
    if (!list[0].count) list[0].count = 1;
    // Shares: the brush footprint's falloff weight that each copy really
    // covers on its own island, per unit of model area. The kernel spreads a
    // copy's share over that copy's footprint, so the paint per unit area is
    // the same on both sides of a seam.
    list.forEach((e) => { e.weight = footprintWeight(e); });
    const total = list.reduce((a, e) => a + e.weight, 0);
    list.forEach((e) => {
      const allowed = allowedMaterial < 0 || map.islandMaterial[e.island] === allowedMaterial;
      e.share = allowed ? (total > 0 ? e.weight / total : (e === list[0] ? 1 : 0)) : 0;
    });
    if (list.length > 1) stats.seamSamples += 1;
    return list;
  };

  // Resolves a hit to its grid position and covered cell (null for missing UVs).
  const locate = (hit) => {
    const grid = gridOf(hit);
    if (!Number.isFinite(grid[0]) || !Number.isFinite(grid[1])) return null;
    const cell = nearestCoveredCell(map, grid[0], grid[1], 3, primaryIsland);
    return cell < 0 ? null : { grid, cell };
  };

  return {
    stats,
    entries: () => entries,
    position: () => v.slice(),

    // First contact: returns the plane position for the engine sample, or null.
    start(hit) {
      const where = locate(hit);
      stats.samples += 1;
      if (!where) { stats.missed += 1; return null; }
      const P = vec(hit.point);
      const N = norm(vec(hit.normal)) || [0, 0, 1];
      // The plane's x axis starts along the grid's x direction, so on an
      // undistorted island the plane and the grid agree.
      const T = (() => {
        const cellPos = (i) => [map.pos[3 * i], map.pos[3 * i + 1], map.pos[3 * i + 2]];
        const x = where.cell % W;
        const right = x + 1 < W && map.island[where.cell + 1] === map.island[where.cell] ? where.cell + 1 : -1;
        const left = x > 0 && map.island[where.cell - 1] === map.island[where.cell] ? where.cell - 1 : -1;
        const a = left >= 0 ? cellPos(left) : cellPos(where.cell);
        const b = right >= 0 ? cellPos(right) : cellPos(where.cell);
        const t = sub(b, a);
        return norm(sub(t, scale(N, dot(N, t)))) || perpendicularTo(N);
      })();
      const B = norm(cross(N, T)) || perpendicularTo(T);
      primaryIsland = map.island[where.cell];
      v = where.grid.slice();
      frame = { P, N, T, B };
      entries = buildEntries(P, N, T, B, where.grid, where.cell);
      return { x: v[0], y: v[1] };
    },

    // Further contact: transports the frame and returns the new plane position, or null.
    move(hit) {
      if (!frame) return this.start(hit);
      const where = locate(hit);
      stats.samples += 1;
      if (!where) { stats.missed += 1; return null; }
      const P = vec(hit.point);
      const N = norm(vec(hit.normal)) || frame.N;
      const d = sub(P, frame.P);
      // Displacement measured in the frame between the two samples.
      const Tm = norm(sub(frame.T, scale(N, dot(N, frame.T)))) || frame.T;
      const Bm = norm(cross(N, Tm)) || frame.B;
      const ta = norm(scale([frame.T[0] + Tm[0], frame.T[1] + Tm[1], frame.T[2] + Tm[2]], 0.5)) || Tm;
      const ba = norm(scale([frame.B[0] + Bm[0], frame.B[1] + Bm[1], frame.B[2] + Bm[2]], 0.5)) || Bm;
      v = [v[0] + dot(d, ta) / h, v[1] + dot(d, ba) / h];
      primaryIsland = map.island[where.cell];
      frame = { P, N, T: Tm, B: Bm };
      entries = buildEntries(P, N, Tm, Bm, where.grid, where.cell);
      return { x: v[0], y: v[1] };
    },

    // Engine stamp (plane coordinates) -> zero or more grid stamps.
    mapStamp(stamp, push) {
      stats.stamps += 1;
      for (const e of entries) {
        if (!(e.share > 0)) continue;
        const qx = stamp.px - e.anchorV[0];
        const qy = stamp.py - e.anchorV[1];
        const [m00, m01, m10, m11] = e.M;
        const gx = e.anchorGrid[0] + m00 * qx + m01 * qy;
        const gy = e.anchorGrid[1] + m10 * qx + m11 * qy;
        const s = Math.sqrt(Math.abs(m00 * m11 - m01 * m10)) || 1;
        const r = Math.max(0.5, (stamp.baseRadius || 1) * s);
        if (gx < -r || gy < -r || gx > W + r || gy > H + r) continue;
        const mapDir = (x, y) => {
          const len = Math.hypot(x, y);
          if (!(len > 0)) return [x, y];
          const mx = m00 * x + m01 * y;
          const my = m10 * x + m11 * y;
          const ml = Math.hypot(mx, my) || 1;
          return [(mx / ml) * len, (my / ml) * len];
        };
        const [ax, ay] = mapDir(stamp.acrossX || 0, stamp.acrossY || 0);
        const [dx, dy] = mapDir(stamp.dirX || 0, stamp.dirY || 0);
        stats.mappedStamps += 1;
        push({
          ...stamp,
          px: gx,
          py: gy,
          baseRadius: r,
          ds: (stamp.ds || 0) * s,
          cover: e.share,
          acrossX: ax,
          acrossY: ay,
          dirX: dx,
          dirY: dy,
          island: e.island,
        });
      }
    },

    // Palette knife steps go onto the island under the brush only.
    mapBlade(step, push) {
      const e = entries[0];
      if (!e) return;
      const qx = step.cx - e.anchorV[0];
      const qy = step.cy - e.anchorV[1];
      const [m00, m01, m10, m11] = e.M;
      const s = Math.sqrt(Math.abs(m00 * m11 - m01 * m10)) || 1;
      const dx = m00 * step.dirX + m01 * step.dirY;
      const dy = m10 * step.dirX + m11 * step.dirY;
      const dl = Math.hypot(dx, dy) || 1;
      push({
        ...step,
        cx: e.anchorGrid[0] + m00 * qx + m01 * qy,
        cy: e.anchorGrid[1] + m10 * qx + m11 * qy,
        ds: step.ds * s,
        dirX: dx / dl,
        dirY: dy / dl,
      });
    },
  };
}
